import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import AdminHeader from "../../components/admin/AdminHeader";

const LEAD_TYPE_BADGE = {
  yard_designer: "bg-[#8C1D30] text-[#F5F3EF]",
  estimator:     "bg-[#0B2B1F] text-[#F5F3EF] border border-[#D8D6D0]/30",
  residential:   "bg-[#1f1f1f] text-[#F5F3EF] border border-[#D8D6D0]/30",
  commercial:    "bg-[#1f1f1f] text-[#F5F3EF] border border-[#D8D6D0]/30",
  general:       "bg-[#151515] text-[#D8D6D0]/70 border border-[#D8D6D0]/20",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => api.get("/admin/leads").then((r) => setLeads(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success("Updated");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Deleted");
    load();
  };

  const yardCount = leads.filter((l) => l.lead_type === "yard_designer").length;
  const visible = filter === "all" ? leads : leads.filter((l) => l.lead_type === filter);

  const filters = [
    { key: "all",            label: `All (${leads.length})` },
    { key: "yard_designer",  label: `AI Designer (${yardCount})` },
    { key: "estimator",      label: `Estimator (${leads.filter((l)=>l.lead_type==="estimator").length})` },
    { key: "residential",    label: `Residential (${leads.filter((l)=>l.lead_type==="residential").length})` },
    { key: "commercial",     label: `Commercial (${leads.filter((l)=>l.lead_type==="commercial").length})` },
    { key: "general",        label: `General (${leads.filter((l)=>l.lead_type==="general").length})` },
  ];

  return (
    <div data-testid="admin-leads">
      <AdminHeader title="Leads" subtitle="Inbound enquiries from the website." count={leads.length} />

      {yardCount > 0 && (
        <div data-testid="leads-yard-banner" className="mb-6 border-l-2 border-[#8C1D30] bg-[#8C1D30]/10 p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-[#F5F3EF]">
            <span className="font-semibold text-[#8C1D30]">{yardCount}</span> lead{yardCount === 1 ? "" : "s"} came from the <span className="font-semibold">AI Yard Designer</span>. Full uploaded concepts, photos and selected designs are in the AI Designs tab.
          </div>
          <Link
            to="/admin/generations"
            data-testid="leads-goto-generations"
            className="px-4 py-2 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-xs uppercase tracking-widest whitespace-nowrap"
          >
            View AI Designs →
          </Link>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5" data-testid="leads-filters">
        {filters.map((f) => (
          <button
            key={f.key}
            data-testid={`leads-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors border ${
              filter === f.key
                ? "bg-[#F5F3EF] text-[#151515] border-[#F5F3EF]"
                : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {visible.length === 0 && (
          <div className="text-sm text-[#D8D6D0]/50 bg-[#151515] border border-[#D8D6D0]/10 p-8 text-center">
            No leads yet.
          </div>
        )}
        {visible.map((l) => {
          const badge = LEAD_TYPE_BADGE[l.lead_type] || LEAD_TYPE_BADGE.general;
          const isYard = l.lead_type === "yard_designer";
          return (
            <div
              key={l.id}
              data-testid={`lead-row-${l.id}`}
              data-lead-type={l.lead_type}
              className={`bg-[#151515] border p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center ${
                isYard ? "border-[#8C1D30]/40" : "border-[#D8D6D0]/10"
              }`}
            >
              <div className="md:col-span-3">
                <div className="font-serif-display text-lg">{l.name}</div>
                <span className={`inline-block mt-1.5 px-2 py-1 text-[10px] uppercase tracking-widest ${badge}`}>
                  {l.lead_type.replace("_", " ")}
                </span>
                {l.marketing_consent && (
                  <span data-testid={`lead-${l.id}-marketing-ok`} className="inline-block mt-1.5 ml-1.5 px-2 py-1 text-[10px] uppercase tracking-widest bg-[#2E7D4F]/20 text-[#5BBF8A] border border-[#2E7D4F]/40" title={`Consented ${l.consent_at ? new Date(l.consent_at).toLocaleString() : ""} via ${l.consent_source || "site"}`}>
                    ✉ Marketing OK
                  </span>
                )}
              </div>
              <div className="md:col-span-4 text-xs text-[#D8D6D0]/80">
                <div>{l.email}</div>
                <div className="text-[#D8D6D0]/55 mt-0.5">{l.phone}</div>
                {l.address && <div className="text-[#D8D6D0]/55 mt-0.5">{l.address}</div>}
              </div>
              <div className="md:col-span-3 text-xs text-[#D8D6D0]/55">
                {l.message ? l.message.substring(0, 120) + (l.message.length > 120 ? "…" : "") : "—"}
                <div className="mt-1.5 text-[10px] text-[#D8D6D0]/35 uppercase tracking-widest">
                  {new Date(l.created_at).toLocaleString()} · {l.source_page || "—"}
                </div>
                {isYard && (
                  <Link
                    to="/admin/generations"
                    data-testid={`lead-${l.id}-goto-design`}
                    className="inline-block mt-2 text-[10px] uppercase tracking-widest text-[#8C1D30] hover:underline"
                  >
                    View AI design →
                  </Link>
                )}
              </div>
              <div className="md:col-span-2 flex gap-2 justify-end">
                <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="bg-[#0d0d0d] border border-[#D8D6D0]/20 px-2 py-1.5 text-xs text-[#F5F3EF]">
                  <option value="new">new</option>
                  <option value="contacted">contacted</option>
                  <option value="qualified">qualified</option>
                  <option value="closed">closed</option>
                </select>
                <button onClick={() => del(l.id)} className="text-xs text-[#8C1D30] hover:underline">Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
